import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import axios from 'axios';
import { trackSignupComplete } from '../utils/analytics';

const AuthContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchingRef = useRef(false);

  // Fetch user profile function
  const fetchUserProfile = useCallback(async (supaUser, accessToken) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    try {
      // Set token state
      setToken(accessToken);
      
      // Set axios default header for API calls
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      // Get user profile from our backend - use /api/profile for full subscription data
      const response = await axios.get(`${API}/profile`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // If user doesn't exist in our DB, create from the passed supaUser data
      if (supaUser) {
        try {
          await axios.post(`${API}/auth/supabase-sync`, {
            supabase_id: supaUser.id,
            email: supaUser.email,
            name: supaUser.user_metadata?.full_name || supaUser.email?.split('@')[0] || 'User'
          });
          // Retry fetching profile after sync
          const retryResponse = await axios.get(`${API}/profile`);
          setUser(retryResponse.data);
        } catch (syncError) {
          console.error('Failed to sync user:', syncError);
          // Fallback to basic user object
          setUser({
            id: supaUser.id,
            email: supaUser.email,
            name: supaUser.user_metadata?.full_name || supaUser.email?.split('@')[0] || 'User',
            scan_count: 0,
            max_scans: 50
          });
        }
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const initAuth = async () => {
      try {
        // First check if we have a session in localStorage (before async call)
        const storedSession = localStorage.getItem('flipvalue-auth-token');
        
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        setSession(session);
        if (session?.user) {
          await fetchUserProfile(session.user, session.access_token);
        } else {
          // No session - user is logged out
          setLoading(false);
        }
      } catch (error) {
        console.error('Init auth error:', error);
        if (mounted) setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth event:', event, session?.user?.email);
      
      if (!mounted) return;
      
      setSession(session);
      
      if (event === 'SIGNED_IN' && session?.user) {
        // Track signup/signin
        const isNewUser = session.user.created_at && 
          (new Date() - new Date(session.user.created_at)) < 60000; // Within 1 minute
        if (isNewUser) {
          trackSignupComplete(session.user.app_metadata?.provider || 'email');
        }
        await fetchUserProfile(session.user, session.access_token);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setToken(null);
        delete axios.defaults.headers.common['Authorization'];
        setLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        setToken(session.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
      } else if (event === 'INITIAL_SESSION' && session?.user) {
        // Handle initial session restoration (e.g., after page refresh)
        await fetchUserProfile(session.user, session.access_token);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  // Email/Password signup - use main supabase client directly
  const signup = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: name }
      }
    });
    
    if (error) throw error;
    
    // Also create user in our backend
    if (data.user) {
      try {
        await axios.post(`${API}/auth/supabase-sync`, {
          supabase_id: data.user.id,
          email: data.user.email,
          name: name
        });
      } catch (e) {
        console.error('Failed to sync user to backend:', e);
      }
    }
    
    return data;
  };

  // Email/Password login - use main supabase client directly
  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle "body stream already read" error gracefully
      if (err.message && err.message.includes('body stream already read')) {
        // This error can occur when:
        // 1. The credentials are invalid and Supabase returns 400
        // 2. There's a network/parsing issue
        
        // Wait a moment and check if we have a session
        await new Promise(resolve => setTimeout(resolve, 500));
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session) {
          return sessionData;
        }
        
        // If no session, the credentials were likely invalid
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }
      
      // Provide user-friendly error messages
      if (err.message?.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }
      if (err.message?.includes('Email not confirmed')) {
        throw new Error('Please check your email and confirm your account before signing in.');
      }
      
      throw err;
    }
  };

  // Google OAuth
  const signInWithGoogle = async () => {
    // Use window.location.origin for OAuth redirects to work across all environments
    const redirectUrl = `${window.location.origin}/auth/callback`;
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl
      }
    });
    
    if (error) throw error;
    return data;
  };

  // Apple OAuth
  const signInWithApple = async () => {
    // Use window.location.origin for OAuth redirects to work across all environments
    const redirectUrl = `${window.location.origin}/auth/callback`;
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: redirectUrl
      }
    });
    
    if (error) throw error;
    return data;
  };

  // Logout
  const logout = async () => {
    await supabase.auth.signOut();
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setSession(null);
    setToken(null);
  };

  const refreshUser = async () => {
    if (session?.user) {
      await fetchUserProfile(session.user, session.access_token);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      session,
      loading, 
      login, 
      signup, 
      signInWithGoogle,
      signInWithApple,
      logout, 
      refreshUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
